import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FlaskConical } from 'lucide-react';
import { Button } from '../../components/new-components/ui/button';
import { SandboxCompanyStep } from './steps/SandboxCompanyStep';
import { SandboxBrandingStep, type BrandingData } from './steps/SandboxBrandingStep';
import { SandboxInvestorsStep } from './steps/SandboxInvestorsStep';
import { SandboxReviewStep } from './steps/SandboxReviewStep';
import { sandboxOnboardingApi } from '../../services/sandboxApi';

// ─────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────
type SandboxStep = 'company' | 'branding' | 'investors' | 'review' | 'success';

const NAVIGABLE_STEPS: Exclude<SandboxStep, 'success'>[] = [
  'company',
  'branding',
  'investors',
  'review',
];

const STEP_LABELS: Record<Exclude<SandboxStep, 'success'>, string> = {
  company: 'Company',
  branding: 'Branding',
  investors: 'Investors',
  review: 'Review',
};

// ─────────────────────────────────────────────
//  Step Indicator
// ─────────────────────────────────────────────
interface StepIndicatorProps {
  currentStep: SandboxStep;
}

function StepIndicator({ currentStep }: StepIndicatorProps) {
  const currentIdx = NAVIGABLE_STEPS.indexOf(currentStep as Exclude<SandboxStep, 'success'>);

  return (
    <div className="flex items-center justify-center gap-0" role="list" aria-label="Wizard steps">
      {NAVIGABLE_STEPS.map((step, idx) => {
        const isComplete = currentIdx > idx;
        const isActive = currentIdx === idx;

        return (
          <React.Fragment key={step}>
            <div
              role="listitem"
              className="flex flex-col items-center gap-1"
              aria-current={isActive ? 'step' : undefined}
            >
              <div
                className={[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
                  isComplete
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                    ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                    : 'bg-muted text-muted-foreground',
                ].join(' ')}
              >
                {isComplete ? <Check className="w-4 h-4" /> : idx + 1}
              </div>
              <span
                className={[
                  'text-xs hidden sm:block',
                  isActive ? 'text-foreground font-medium' : 'text-muted-foreground',
                ].join(' ')}
              >
                {STEP_LABELS[step]}
              </span>
            </div>

            {/* Connector line */}
            {idx < NAVIGABLE_STEPS.length - 1 && (
              <div
                className={[
                  'h-px flex-1 mx-2 mb-4 transition-colors',
                  isComplete ? 'bg-primary' : 'bg-border',
                ].join(' ')}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Main Component
// ─────────────────────────────────────────────
export default function SandboxSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState<SandboxStep>('company');
  const [isVisible, setIsVisible] = useState(true);

  // Step data
  const [companyName, setCompanyName] = useState('');
  const [slug, setSlug] = useState('');
  const [branding, setBranding] = useState<BrandingData>({
    logoFile: null,
    logoPreviewUrl: null,
    primaryColor: '#1a1a2e',
    secondaryColor: '#e94560',
    fontFamily: 'Inter',
  });
  const [adminEmails, setAdminEmails] = useState<string[]>(['']);
  const [investorEmails, setInvestorEmails] = useState<string[]>(['']);

  // Launch state
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchResult, setLaunchResult] = useState<{
    sandboxTenantId: string;
    trialExpiresAt: string;
  } | null>(null);

  /** Cross-fade transition helper */
  const navigate_step = useCallback((to: SandboxStep) => {
    setIsVisible(false);
    setTimeout(() => {
      setStep(to);
      setIsVisible(true);
    }, 150);
  }, []);

  const handleBrandingChange = useCallback((update: Partial<BrandingData>) => {
    setBranding((prev) => ({ ...prev, ...update }));
  }, []);

  async function handleLaunch() {
    setIsLaunching(true);
    setLaunchError(null);

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = investorEmails.filter((e) => EMAIL_RE.test(e));
    const validAdmins = adminEmails.filter((e) => EMAIL_RE.test(e));

    try {
      // Upload logo to S3 via presigned URL if a file was selected
      let logoS3Key: string | undefined;
      let logoS3Bucket: string | undefined;
      if (branding.logoFile) {
        const uploadRes = await sandboxOnboardingApi.getBrandingUploadUrl({
          filename: branding.logoFile.name,
          mimeType: branding.logoFile.type || 'image/png',
        });
        if (uploadRes.success && uploadRes.data) {
          await fetch(uploadRes.data.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': branding.logoFile.type || 'image/png' },
            body: branding.logoFile,
          });
          logoS3Key = uploadRes.data.s3Key;
          logoS3Bucket = uploadRes.data.s3Bucket;
        }
      }

      const res = await sandboxOnboardingApi.launch({
        companyName,
        slug,
        primaryColor: branding.primaryColor,
        accentColor: branding.secondaryColor,
        fontFamily: branding.fontFamily,
        logoS3Key,
        logoS3Bucket,
        investorEmails: validEmails,
        adminEmails: validAdmins,
      });
      if (res.success && res.data) {
        setLaunchResult(res.data);
        navigate_step('success');
      } else {
        setLaunchError(res.message || res.error || 'Launch failed. Please try again.');
      }
    } catch {
      setLaunchError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLaunching(false);
    }
  }

  function formatExpiry(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-4 md:px-6 py-4 flex items-center gap-3">
        <FlaskConical className="w-5 h-5 text-primary" />
        <h1 className="text-lg font-semibold text-foreground">New Sandbox</h1>
      </header>

      {/* Step Indicator — only for non-success steps */}
      {step !== 'success' && (
        <div className="px-4 md:px-6 pt-6 pb-2 max-w-lg mx-auto w-full">
          <StepIndicator currentStep={step} />
        </div>
      )}

      {/* Content */}
      <main className="flex-1 flex items-start justify-center px-4 md:px-6 py-8">
        <div
          className="w-full max-w-lg transition-opacity duration-150"
          style={{ opacity: isVisible ? 1 : 0 }}
        >
          {step === 'company' && (
            <SandboxCompanyStep
              companyName={companyName}
              slug={slug}
              adminEmails={adminEmails}
              onCompanyNameChange={setCompanyName}
              onSlugChange={setSlug}
              onAdminEmailsChange={setAdminEmails}
              onContinue={() => navigate_step('branding')}
            />
          )}

          {step === 'branding' && (
            <SandboxBrandingStep
              companyName={companyName}
              branding={branding}
              onChange={handleBrandingChange}
              onContinue={() => navigate_step('investors')}
              onBack={() => navigate_step('company')}
            />
          )}

          {step === 'investors' && (
            <SandboxInvestorsStep
              emails={investorEmails}
              onChange={setInvestorEmails}
              onContinue={() => navigate_step('review')}
              onBack={() => navigate_step('branding')}
              onSkip={() => navigate_step('review')}
            />
          )}

          {step === 'review' && (
            <SandboxReviewStep
              companyName={companyName}
              slug={slug}
              branding={branding}
              investorEmails={investorEmails}
              isLaunching={isLaunching}
              launchError={launchError}
              onLaunch={handleLaunch}
              onBack={() => navigate_step('investors')}
            />
          )}

          {step === 'success' && launchResult && (
            <div className="text-center space-y-6 py-8">
              {/* Green checkmark */}
              <div className="mx-auto w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-10 h-10 text-green-600" />
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-foreground">Sandbox launched!</h2>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">{companyName}</span> is ready.
                </p>
              </div>

              <div className="rounded-lg bg-muted/60 border border-border px-4 py-3 text-sm text-muted-foreground inline-block">
                Trial expires:{' '}
                <span className="font-medium text-foreground">
                  {formatExpiry(launchResult.trialExpiresAt)}
                </span>
              </div>

              <div className="pt-2">
                <Button
                  onClick={() => navigate('/admin/dashboard')}
                  className="w-full max-w-xs mx-auto"
                  size="lg"
                >
                  Go to Admin Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
